/**
 * The notice draft, as the form holds it and as the endpoints take it (#1911).
 *
 * A module rather than part of the form component because three surfaces read
 * it — the copy section, the audience section, and the drawer's save button —
 * and because every rule in here is about the SHAPE of a request rather than
 * about rendering.
 *
 * ── WHY THE AUDIENCE IS NESTED AND NOT FLATTENED ────────────────────────────
 * `models` and `windowDays` could sit next to `subject` and the form would be
 * shorter. They do not, because `NoticeAudience` is a discriminated union and
 * the second member is a matter of when, not if. A flattened draft makes the
 * second strategy a rewrite of every field, every validator and every caller;
 * nested, it is one more `case` in {@link toAudience} and one more branch in
 * the audience section. The cost of the extra level is paid once, here.
 */
import type {
  CreateEmailNoticeRequest,
  EmailNoticeModelRef,
  NoticeAudience,
  NoticeAudienceType,
} from '@nadoutong/contracts';
import {
  EMAIL_NOTICE_DEFAULT_WINDOW_DAYS,
  EMAIL_NOTICE_MAX_WINDOW_DAYS,
  EMAIL_NOTICE_PLACEHOLDERS,
  unknownPlaceholders,
} from '@nadoutong/contracts';

/**
 * The editing form of {@link NoticeAudience}.
 *
 * Distinct from the wire type for one reason: `window_days` is a number there
 * and a partially-typed string here. An `<input type="number">` is empty, or
 * `"3"` on the way to `"30"`, and coercing mid-keystroke would either reject
 * what the operator is still typing or send a window they did not mean.
 */
export type AudienceDraft = {
  readonly type: 'models_used';
  readonly models: readonly EmailNoticeModelRef[];
  readonly windowDays: string;
};

export interface NoticeDraft {
  /** Internal label. Never leaves the console. */
  readonly taskTitle: string;
  /** The mail's Subject header — what a paying customer reads. */
  readonly subject: string;
  readonly body: string;
  readonly audience: AudienceDraft;
  /** `YYYY-MM-DD` or `''`. A reminder; it drives nothing. */
  readonly remindOn: string;
}

export const EMPTY_AUDIENCE: AudienceDraft = {
  type: 'models_used',
  models: [],
  windowDays: String(EMAIL_NOTICE_DEFAULT_WINDOW_DAYS),
};

export const EMPTY_DRAFT: NoticeDraft = {
  taskTitle: '',
  subject: '',
  body: '',
  audience: EMPTY_AUDIENCE,
  remindOn: '',
};

/** A blank draft for `type`, used when the strategy dropdown changes. */
export function emptyAudienceFor(type: NoticeAudienceType): AudienceDraft {
  switch (type) {
    case 'models_used':
      return EMPTY_AUDIENCE;
  }
}

/**
 * The audience as the endpoints take it, or `null` while it is incomplete.
 *
 * `null` is "not ready", not "invalid" — the operator is mid-compose and the
 * save button is simply not live yet. The server re-checks all of it with
 * `isNoticeAudience`; this exists so an incomplete draft never leaves.
 */
export function toAudience(draft: AudienceDraft): NoticeAudience | null {
  switch (draft.type) {
    case 'models_used': {
      if (draft.models.length === 0) return null;
      const windowDays = Number.parseInt(draft.windowDays, 10);
      if (!Number.isInteger(windowDays)) return null;
      if (windowDays <= 0 || windowDays > EMAIL_NOTICE_MAX_WINDOW_DAYS) return null;
      return { type: 'models_used', models: draft.models, window_days: windowDays };
    }
  }
}

/**
 * Whether two audiences ask the same question.
 *
 * ── WHY THE CONSOLE NEEDS THIS AT ALL ───────────────────────────────────────
 * The preview (#1920) answers for the audience in the FORM; the freeze
 * recomputes server-side from the audience that is STORED. Before the preview
 * escaped the draft those were the same value by construction. They are not any
 * more, and the gap is a trap with a specific shape: change the model selection
 * without saving, preview it, read a count of forty, press freeze — and the
 * server freezes the twenty-five the stored audience resolves to.
 *
 * The frozen table would eventually show that, which is the point of rendering
 * it again before any send. But "the operator finds out at the last screen that
 * the number they were working from was never the one" is a worse outcome than
 * refusing the freeze while the two disagree, so the freeze is refused.
 *
 * Model order is not significant — the picker appends in click order and the
 * stored row comes back in the server's — so the comparison sorts first.
 */
export function sameAudience(
  left: NoticeAudience | null,
  right: NoticeAudience | null,
): boolean {
  if (left === null || right === null) return left === right;
  if (left.type !== right.type) return false;
  if (left.window_days !== right.window_days) return false;
  const key = (audience: NoticeAudience): string =>
    audience.models.map((ref) => `${ref.vendor}/${ref.model}`).sort().join('\n');
  return key(left) === key(right);
}

/**
 * Placeholders the copy uses that a notice is not allowed to use.
 *
 * Empty means safe to save. Checked in front of the operator because the
 * server's refusal (422 `EMAIL_TEMPLATE_UNKNOWN_PLACEHOLDER`) arrives after
 * they have stopped looking at the word they mistyped.
 */
export function draftUnknownPlaceholders(draft: NoticeDraft): readonly string[] {
  return unknownPlaceholders(draft.subject, draft.body, EMAIL_NOTICE_PLACEHOLDERS);
}

/**
 * The draft as the create/update endpoints take it, or `null` when it is not
 * yet complete.
 *
 * `task_title` and `subject` are carried separately and trimmed separately.
 * They are two different sentences for two different readers — the operator
 * scanning forty rows, and the customer reading an inbox — and the entire
 * point of splitting them is lost the moment one is derived from the other.
 */
export function toNoticeRequest(draft: NoticeDraft): CreateEmailNoticeRequest | null {
  const audience = toAudience(draft.audience);
  if (audience === null) return null;
  return {
    task_title: draft.taskTitle.trim(),
    subject: draft.subject.trim(),
    body: draft.body.trim(),
    audience,
    remind_on: draft.remindOn === '' ? null : draft.remindOn,
  };
}

/** Every field the endpoints require is present, non-blank and legal. */
export function isDraftSaveable(draft: NoticeDraft): boolean {
  return (
    draft.taskTitle.trim() !== '' &&
    draft.subject.trim() !== '' &&
    draft.body.trim() !== '' &&
    toAudience(draft.audience) !== null &&
    draftUnknownPlaceholders(draft).length === 0
  );
}
